import React, {
  useState,
  useEffect,
  Suspense,
  Fragment,
} from "react";
import { Switch, Redirect, Route, Link } from "react-router-dom";
import { connect } from "react-redux";
import { ToastContainer } from "react-toastify";

import PropTypes from "prop-types";
import ClassNames from "classnames";
import Config from "config";
import PROFILE_FIELD from "Constants/profileFields";
import ROUTES from "Constants/routes";
import PageNotFound from "Containers/404";
import Dayjs from "dayjs";

import Button, { BUTTON_TYPE } from "@propellerads/button";
import { Burger } from "@propellerads/icon";
import Header from "@propellerads/header";

import UserService from "Services/user";
import ProfileService from "Services/profile";
import FullStory from "Services/fullstory";
import I18nService, { AVAILABLE_LANGUAGES } from "Services/i18n";
import AnalyticsService, { EVENT } from "Services/analytics";
import { sendUserData } from "Services/intercom";
import WebSocketService from "Services/websocket";
import { init as googleTagManagerInit } from "Services/googleTagManager";

import Sidebar from "Components/Sidebar";
import Footer from "Components/Footer";
import SplashScreen from "Components/SplashScreen";
import RouteContainer from "Components/Route";
import AutoLoginPanel from "Components/AutoLoginPanel";
import Terms from "Components/Terms";
import Translate from "Components/Translate";

import SingleNotificationList from "Components/Notifications/SingleNotificationList";
import { ReactComponent as DesktopLogo } from "Components/Layout/desktop_logo.svg";

import { get, update } from "actions/profile";
import { logout } from "../../reducers/authorizeSlice";
import { load } from "actions/audiences";
import { createSendingClick } from "actions/sendings";

import "./style.scss";
import CountersService from "Services/counters";
import Popup from "@propellerads/popup";
import { loadSubscriptionsFilters } from "../../actions/subscriptions";
import { formatCount } from "../../Services/counters";
import moment from "moment";
import NotixTag from "../NotixTag";
import { getCountries } from "../../reducers/countriesSlice";
import { getCountersActiveUsersZones } from "../../reducers/countersSlice";
import { loadNotixProfile } from "../../reducers/notixProfileSlice";
import { getZones } from "../../reducers/zonesSlice";
import { getAdvZones } from "../../reducers/zonesAdvSlice";
import NotixHeader from "../NotixHeader";
import { useWindowSize } from "../../utils/useWindowSize";
import NotixAlert from "../../Containers/NotixAlert";
import {
  getBrowser,
  getDevice,
  getLanguage,
  getOs,
} from "../../actions/targeting";
import lazyRetry from "../../utils/lazyRetry";
import {useAppDispatch, useAppSelector} from "../../store/configureStore";
import {loadActiveSubscribers} from "../../reducers/statisticSlice";
import EnvironmentBlock from "../EnvironmentBlock";

const SignInContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/SignIn"), "SignIn")
);
const RegistrationClosedContainer = React.lazy(() =>
    lazyRetry(() => import("Containers/RegistrationClosed"), "RegistrationClosed")
);
const ProfileContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Profile"), "Profile")
);
const StatisticsContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Statistics"), "Statistics")
);
const EmailVerificationContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/EmailVerification"), "EmailVerification")
);
const ActivateAccountContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/ActivateAccount"), "ActivateAccount")
);
const SiteListContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/SiteList"), "SiteList")
);
const AddSourceTagContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/AddSourceTag"), "AddSourceTag")
);
const SiteContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Site"), "Site")
);
const GetTagContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/GetTag"), "GetTag")
);
const ForgotPasswordContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/ForgotPassword"), "ForgotPassword")
);
const AudiencesContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Audiences"), "Audiences")
);
const CreateSendingContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/CreateSending"), "CreateSending")
);
const RssFormContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/CreateSending/RssForm"), "RssForm")
);
const InvoicesContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Invoices"), "Invoices")
);
const BillingContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Billing"), "Billing")
);
const SendingsContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Sendings"), "Sendings")
);
const WelcomePageContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/WelcomePage"), "WelcomePage")
);
const TestInvisiblePageContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/TestInvisible"), "TestInvisible")
);
const ApiAccessContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Profile/ApiAccess"), "ApiAccess")
);
const SubscriptionsContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Subscriptions"), "Subscriptions")
);
const IntegrationsContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/Integrations"), "Integrations")
);
const AudiencePixelsContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/AudiencePixels"), "AudiencePixels")
);
const AdvStatisticsContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/AdvStatistics"), "AdvStatistics")
);
const RssListContainer = React.lazy(() =>
  lazyRetry(() => import("Containers/RssList"), "RssList")
);

const Logo = () => (
  <Link to="/">
    <DesktopLogo className="App__logo" />
  </Link>
);

const Layout = ({
  isAuthenticated,
  logoutAction,
  isReadOnly,
  tokenExpiresIn,
  loginAt,
  getProfileAction,
  profile,
  isOurTermsAccepted,
  updateProfileAction,
  isLogoutSuccess,
  isLogoutLoading,
  listCountriesAction,
  listAudiencesAction,
  getZonesAction,
  getAdvZonesAction,
  getCountersActiveUsersZonesAction,
  loadSubscriptionsFiltersAction,
  getNotixProfileAction,
  isEnabledBilling,
  getOsAction,
  getDeviceAction,
  getBrowserAction,
  getLanguageAction,
}) => {
  const { activeSubscribers } = useAppSelector((store) => store.stats);
  const dispatch = useAppDispatch();

  const [defaultLanguage, setDefaultLanguageHook] = useState(
    I18nService.getDefaultLanguage()
  );

  const [sendingTotal, setSendingTotal] = useState(0);
  const [windowSize] = useWindowSize();
  const [isSidebarOpen, setSidebar] = useState(windowSize < 900);
  const [userTime, setUserTime] = useState(moment().format("HH:mm"));

  useEffect(() => {
    if (windowSize < 900) {
      setSidebar(false);
    } else {
      setSidebar(true);
    }
  }, [windowSize]);

  const languageTitle = I18nService.getLanguageTitle(defaultLanguage);
  const shortLanguageTitle = languageTitle.toUpperCase().slice(0, 2);
  const countdownTime = loginAt + tokenExpiresIn - new Dayjs().unix();

  const style = ClassNames("App", {
    "App_aside-left": isAuthenticated && !isSidebarOpen,
    "App_aside-left-is-open-sidebar": isAuthenticated && isSidebarOpen,
  });

  const user = UserService.get();

  async function init() {
    const user = await UserService.get();
    const profile = await ProfileService.get();
    await AnalyticsService.trackUserId(user.id);
    await FullStory.targetingInit();

    const userFullName = `${profile[PROFILE_FIELD.FIRST_NAME]} ${
      profile[PROFILE_FIELD.LAST_NAME]
    }`;

    if (Config.websocket) {
      WebSocketService.addInitializer(() => {
        WebSocketService.send("identify", {
          customerId: user.id,
          customerType: "publisher",
        });
      }).connect();
    }

    return sendUserData(
      profile.email,
      userFullName,
      Config.role,
      user.id,
      Config.partnerAlias
    );
  }

  useEffect(() => {
    async function counterSendingTotal() {
      try {
        const countSendingTotal = await CountersService.getSendingTotal();
        const count = formatCount(countSendingTotal.count);
        setSendingTotal(count);
      } catch {
        setSendingTotal("");
      }
    }

    if (isAuthenticated) {
      counterSendingTotal();
      setInterval(() => counterSendingTotal(), 1000 * 60);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadActiveSubscribers());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const time = moment().format("HH:mm");
      setUserTime(time);
    }, 60000);

    // eslint-disable-next-line no-shadow
    async function init() {
      try {
        await googleTagManagerInit();
        await FullStory.randomInit();
      } catch {
        // eslint-disable-next-line no-console
        console.error("some scripts is not initialized");
      }
    }

    init();

    const intercomIFrame =
      document.querySelector(".intercom-launcher-frame") ||
      document.querySelector(".intercom-lightweight-app");
    if (intercomIFrame) {
      intercomIFrame.style.zIndex = "9999999";
    }

    return () => clearTimeout(interval);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      init();
      getProfileAction();
      listCountriesAction();
      getOsAction();
      getDeviceAction();
      getBrowserAction();
      getLanguageAction();
      listAudiencesAction();
      getZonesAction();
      getAdvZonesAction();
      getCountersActiveUsersZonesAction();
      loadSubscriptionsFiltersAction();
      getNotixProfileAction();
    }
  }, [
    isAuthenticated,
    getProfileAction,
    listCountriesAction,
    getOsAction,
    getDeviceAction,
    getBrowserAction,
    getLanguageAction,
    listAudiencesAction,
    getZonesAction,
    getAdvZonesAction,
    getCountersActiveUsersZonesAction,
    loadSubscriptionsFiltersAction,
    getNotixProfileAction,
  ]);

  const [sitesItems] = useState([]);
  const [isLoadingSites, setLoadingSitesState] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      setLoadingSitesState(false);
    }
  }, [isAuthenticated]);

  async function setDefaultLanguage(iso) {
    await I18nService.getStrings(iso);
    await I18nService.setDefaultLanguage(iso);

    setDefaultLanguageHook(iso);
  }

  async function acceptOurTerms(profile) {
    await updateProfileAction(
      {
        ...profile,
        [PROFILE_FIELD.TERMS_AND_CONDITIONS_OURS_AGREED_AT]: Date.now(),
      },
      true
    );
  }

  const getFooterLinks = () => [
    Config.footerLinks.help_center,
    Config.footerLinks.docs,
    // Config.footerLinks[I18nService.getDefaultLanguage() === LANG_EN_KEY ? 'tg_en' : 'tg_ru'],
  ];

  if (isLogoutSuccess) {
    if (window.Intercom) {
      window.Intercom("shutdown");
    }
  }

  if (isLogoutLoading) {
    return <SplashScreen />;
  }

  if (isReadOnly) {
    AnalyticsService.updateManagerCookie();
  }

  const routes = [
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SIGN_IN,
      component: SignInContainer,
      gtmEventCategory: EVENT.CATEGORY.LOGIN,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SIGN_UP,
      component: RegistrationClosedContainer,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.REGISTRATION_CLOSED,
      component: RegistrationClosedContainer,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.AUTH.CHECK_EMAIL,
      component: EmailVerificationContainer,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.AUTH.ACTIVATE_ACCOUNT,
      component: ActivateAccountContainer,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.AUTH.RESET_PASSWORD,
      component: ActivateAccountContainer,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.AUTH.FORGOT_PASSWORD,
      component: ForgotPasswordContainer,
      isNeedAuthorization: false,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.PROFILE,
      component: ProfileContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.PROFILE,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.STATISTICS.BASE,
      component: StatisticsContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.STATISTICS,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SITES.LIST,
      component: SiteListContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.SITES,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SITES.CREATE,
      component: AddSourceTagContainer,
      isNeedAuthorization: true,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SITES.ITEM,
      component: SiteContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.ZONES,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SITES.GET_TAG,
      component: GetTagContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.GET_TAG,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.AUDIENCES,
      component: AudiencesContainer,
      isNeedAuthorization: true,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.CREATE_SENDING,
      component: CreateSendingContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.MESSAGES,
    },
    {
      authorized: isAuthenticated,
      //exact: true,
      path: ROUTES.SENDINGS,
      component: SendingsContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.MESSAGES,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.AUTH.API_ACCESS,
      component: ApiAccessContainer,
      isNeedAuthorization: true,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.SUBSCRIPTIONS,
      component: SubscriptionsContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.SUBSCRIPTIONS,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.INTEGRATIONS,
      component: IntegrationsContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.INTEGRATIONS,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.PIXELS,
      component: AudiencePixelsContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.PIXELS,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.STATISTICS.ADV,
      component: AdvStatisticsContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.ADV_STATISTICS,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.CREATE_RSS,
      component: RssFormContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.MESSAGES,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.INVOICES,
      component: InvoicesContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.INVOICES,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.BILLING,
      component: BillingContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.BILLING,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.WELCOME,
      component: WelcomePageContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.WELCOME,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.TEST,
      component: TestInvisiblePageContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.WELCOME,
    },
    {
      authorized: isAuthenticated,
      exact: true,
      path: ROUTES.RSS_LIST,
      component: RssListContainer,
      isNeedAuthorization: true,
      gtmEventCategory: EVENT.CATEGORY.RSS_LIST,
    },
  ];

  const userMenu = [
    <Link to={ROUTES.PROFILE}>
      <Translate tag="menu_personal-settings" />
    </Link>,
    <div id="header_logout--button" onClick={() => logoutAction()}>
      <Translate tag="header_logout" />
    </div>,
  ];

  const SidebarButton = isAuthenticated ? (
    <button
      type="button"
      className="App__sidebarButton"
      onClick={() => setSidebar(!isSidebarOpen)}
    >
      <Burger color="gray" />
    </button>
  ) : null;

  const languagesMenu = AVAILABLE_LANGUAGES.map((lang) => (
    <button
      id={`header_language-${lang.iso}--button`}
      onClick={() => setDefaultLanguage(lang.iso)}
    >
      {lang.title}
    </button>
  ));

  return (
    <Fragment>
      <NotixTag isAuthenticated={isAuthenticated} user={user} />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnVisibilityChange
        draggable
        pauseOnHover
      />
      <div className={style}>
        <Header logo={<Logo />} sidebarButton={SidebarButton}>
          <NotixHeader
            isLoadingSites={isLoadingSites}
            sitesItems={sitesItems}
            shortLanguageTitle={shortLanguageTitle}
            sendingTotal={sendingTotal}
            activeUsers={formatCount(activeSubscribers?.data)}
            userTime={userTime}
            languagesMenu={languagesMenu}
            userMenu={userMenu}
          />
        </Header>
        <div className="App__content">
          {isAuthenticated && (
            <Sidebar
              isEnabledBilling={isEnabledBilling}
              isOpen={isSidebarOpen}
              onClose={() => setSidebar(false)}
              profile={profile}
            />
          )}
          <div
            className={`App__container ${
              isSidebarOpen ? "App__container--opened" : ""
            }`}
          >
            <div className="App__container-alerts">
              <div className="App__container-alert">
                <AutoLoginPanel
                  isAuthenticated={isAuthenticated}
                  isReadOnly={isReadOnly}
                  logoutAction={logoutAction}
                  countdownTime={countdownTime}
                  email={profile?.[PROFILE_FIELD.EMAIL]}
                  userId={user !== undefined ? user.id : undefined}
                />
                <NotixAlert />
              </div>
              {isAuthenticated && (
                <SingleNotificationList
                  isWebsocketAvailable={Config.websocket}
                />
              )}
            </div>
            <Suspense fallback={<SplashScreen />}>
              <Switch>
                <Route
                  exact
                  path="/"
                  render={() =>
                    isAuthenticated ? (
                      <Redirect to={ROUTES.SITES.LIST} />
                    ) : (
                      <Redirect to={ROUTES.SIGN_IN} />
                    )
                  }
                />

                {routes.map((route) => (
                  <RouteContainer
                    Authorized={isAuthenticated}
                    exact={route.exact}
                    path={route.path}
                    component={route.component}
                    gtmEventCategory={route.gtmEventCategory}
                    isNeedAuthorization={route.isNeedAuthorization}
                    key={route.path}
                  />
                ))}

                <Route component={PageNotFound} />
              </Switch>
            </Suspense>
          </div>
        </div>
        <Footer links={getFooterLinks()} />
        {!isOurTermsAccepted && (
          <Popup
            header="Notix Client Agreement"
            footer={
              <Button
                elementId="sds"
                type={BUTTON_TYPE.PRIMARY}
                onClick={() => acceptOurTerms(profile)}
              >
                Agree
              </Button>
            }
            closePopupHandler={() => console.log("close")}
          >
            <Terms />
          </Popup>
        )}
      </div>
      <EnvironmentBlock />
    </Fragment>
  );
};

Layout.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  logoutAction: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  tokenExpiresIn: PropTypes.number,
  loginAt: PropTypes.number,
  getProfileAction: PropTypes.func.isRequired,
  updateProfileAction: PropTypes.func.isRequired,
  isLogoutSuccess: PropTypes.bool.isRequired,
  isLogoutLoading: PropTypes.bool.isRequired,
  listCountriesAction: PropTypes.func.isRequired,
  listAudiencesAction: PropTypes.func.isRequired,
  getZonesAction: PropTypes.func.isRequired,
  getAdvZonesAction: PropTypes.func.isRequired,
  createSendingClickAction: PropTypes.func.isRequired,
  getCountersActiveUsersZonesAction: PropTypes.func.isRequired,
  isEnabledBilling: PropTypes.bool,
};

Layout.defaultProps = {
  isReadOnly: false,
  tokenExpiresIn: 0,
};

const mapStateToProps = (store) => {
  return {
    isAuthenticated: store.authorize.isAuthenticated,
    isReadOnly: store.authorize.isReadOnly,
    tokenExpiresIn: store.authorize.tokenExpiresIn,
    loginAt: store.authorize.loginAt,
    isOurTermsAccepted: store.profile.isOurTermsAccepted,
    profile: store.profile.data,
    isLogoutSuccess: store.authorize.logoutSuccess,
    isLogoutLoading: store.authorize.loading,
    isEnabledBilling: store.notixProfile.enabled,
  };
};
//
const mapDispatchToProps = (dispatch) => {
  return {
    logoutAction: () => dispatch(logout()),
    getProfileAction: () => dispatch(get()),
    updateProfileAction: (profile, isAcceptOurTerms) =>
      dispatch(update(profile, isAcceptOurTerms)),
    listCountriesAction: () => dispatch(getCountries()),
    listAudiencesAction: () => dispatch(load()),
    getZonesAction: () => dispatch(getZones()),
    getAdvZonesAction: () => dispatch(getAdvZones()),
    createSendingClickAction: () => dispatch(createSendingClick()),
    getCountersActiveUsersZonesAction: () =>
      dispatch(getCountersActiveUsersZones()),
    loadSubscriptionsFiltersAction: () => dispatch(loadSubscriptionsFilters()),
    getNotixProfileAction: () => dispatch(loadNotixProfile()),

    getOsAction: () => dispatch(getOs()),
    getDeviceAction: () => dispatch(getDevice()),
    getBrowserAction: () => dispatch(getBrowser()),
    getLanguageAction: () => dispatch(getLanguage()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
